import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from 'react-query';
import apiClient from '../../request/http-common';
import {
    Container,
    Typography,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    Chip,
    Box,
    TextField,
    InputAdornment,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Button,
    Alert,
    CircularProgress,
    useTheme,
    useMediaQuery,
    Card,
    CardContent,
    Grid
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import VisibilityIcon from '@mui/icons-material/Visibility';
import BusinessIcon from '@mui/icons-material/Business';
import PersonIcon from '@mui/icons-material/Person';
import EmailIcon from '@mui/icons-material/Email';

const ListInvestorSubscriptions = () => {
    const navigate = useNavigate();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('');

    const { data: subscriptionsData, isLoading, error, refetch } = useQuery(
        ['investor-subscriptions', page + 1, rowsPerPage, searchTerm, statusFilter],
        () => fetchSubscriptions(),
        { 
            keepPreviousData: true,
            refetchOnWindowFocus: false
        }
    );

    const fetchSubscriptions = async () => {
        const params = new URLSearchParams({
            page: page + 1,
            per_page: rowsPerPage,
            ...(searchTerm && { search: searchTerm }),
            ...(statusFilter && { status: statusFilter })
        });

        console.log('🔍 Fetching subscriptions from:', `${apiClient.defaults.baseURL}/api/investor/admin/subscriptions?${params}`);
        const response = await apiClient.get(`/api/investor/admin/subscriptions?${params}`);
        console.log('✅ Subscriptions response:', response.data);
        return response.data.data;
    };

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const handleSearch = (event) => {
        setSearchTerm(event.target.value);
        setPage(0);
    };

    const handleStatusFilter = (event) => {
        setStatusFilter(event.target.value);
        setPage(0);
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'pending': return 'warning';
            case 'reviewing': return 'info';
            case 'approved': return 'success';
            case 'rejected': return 'error';
            case 'subscription_submitted': return 'primary';
            default: return 'default';
        }
    };

    const formatStatus = (status) => {
        switch (status) {
            case 'subscription_submitted': return 'Submitted';
            default: return status?.charAt(0).toUpperCase() + status?.slice(1) || 'Unknown';
        }
    };

    const handleViewSubscription = (subscriptionId) => {
        navigate(`/admin/investor-subscriptions/${subscriptionId}`);
    };

    if (isLoading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                <CircularProgress />
            </Box>
        );
    }

    if (error) {
        return (
            <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
                <Alert severity="error">
                    Error loading investor subscriptions. Please try again.
                </Alert>
            </Container>
        );
    }

    const subscriptions = subscriptionsData?.data || [];
    const total = subscriptionsData?.total || 0;

    if (isMobile) {
        return (
            <Container maxWidth="lg" sx={{ mt: 2, mb: 4, px: 1 }}>
                <Typography variant="h5" gutterBottom sx={{ px: 1 }}>
                    Investor Subscriptions
                </Typography>

                <Box sx={{ mb: 2, px: 1 }}>
                    <TextField
                        fullWidth
                        placeholder="Search by email, name, or organization..."
                        value={searchTerm}
                        onChange={handleSearch}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon />
                                </InputAdornment>
                            ),
                        }}
                        sx={{ mb: 2 }}
                    />
                    <FormControl fullWidth>
                        <InputLabel>Filter by Status</InputLabel>
                        <Select
                            value={statusFilter}
                            onChange={handleStatusFilter}
                            label="Filter by Status"
                        >
                            <MenuItem value="">All Statuses</MenuItem>
                            <MenuItem value="pending">Pending</MenuItem>
                            <MenuItem value="reviewing">Reviewing</MenuItem>
                            <MenuItem value="subscription_submitted">Submitted</MenuItem>
                            <MenuItem value="approved">Approved</MenuItem>
                            <MenuItem value="rejected">Rejected</MenuItem>
                        </Select>
                    </FormControl>
                </Box>

                {subscriptions.map((subscription) => (
                    <Card key={subscription.id} sx={{ mb: 2 }}>
                        <CardContent>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                                <Typography variant="h6" component="div">
                                    {subscription.organization_name || subscription.fund_name || 'Individual Application'}
                                </Typography>
                                <Chip 
                                    label={formatStatus(subscription.status)} 
                                    color={getStatusColor(subscription.status)} 
                                    size="small"
                                />
                            </Box>
                            
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                <EmailIcon sx={{ mr: 1, fontSize: 16, color: 'text.secondary' }} />
                                <Typography variant="body2" color="text.secondary">
                                    {subscription.user?.email || 'No email'}
                                </Typography>
                            </Box>
                            
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                <PersonIcon sx={{ mr: 1, fontSize: 16, color: 'text.secondary' }} />
                                <Typography variant="body2" color="text.secondary">
                                    {subscription.user?.name || 'No name'}
                                </Typography>
                            </Box>
                            
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                Applied: {new Date(subscription.created_at).toLocaleDateString()}
                            </Typography>
                            
                            <Button
                                variant="contained"
                                size="small"
                                startIcon={<VisibilityIcon />}
                                onClick={() => handleViewSubscription(subscription.id)}
                                fullWidth
                            >
                                View Details
                            </Button>
                        </CardContent>
                    </Card>
                ))}

                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                    <TablePagination
                        component="div"
                        count={total}
                        page={page}
                        onPageChange={handleChangePage}
                        rowsPerPage={rowsPerPage}
                        onRowsPerPageChange={handleChangeRowsPerPage}
                        labelRowsPerPage="Per page:"
                    />
                </Box>
            </Container>
        );
    }

    return (
        <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
            <Typography variant="h4" gutterBottom>
                Investor Subscriptions
            </Typography>

            <Box sx={{ mb: 3, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <TextField
                    placeholder="Search by email, name, or organization..."
                    value={searchTerm}
                    onChange={handleSearch}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon />
                            </InputAdornment>
                        ),
                    }}
                    sx={{ minWidth: 300 }}
                />
                <FormControl sx={{ minWidth: 200 }}>
                    <InputLabel>Filter by Status</InputLabel>
                    <Select
                        value={statusFilter}
                        onChange={handleStatusFilter}
                        label="Filter by Status"
                    >
                        <MenuItem value="">All Statuses</MenuItem>
                        <MenuItem value="pending">Pending</MenuItem>
                        <MenuItem value="reviewing">Reviewing</MenuItem>
                        <MenuItem value="subscription_submitted">Submitted</MenuItem>
                        <MenuItem value="approved">Approved</MenuItem>
                        <MenuItem value="rejected">Rejected</MenuItem>
                    </Select>
                </FormControl>
            </Box>

            <TableContainer component={Paper}>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell>Organization/Fund</TableCell>
                            <TableCell>Contact</TableCell>
                            <TableCell>Status</TableCell>
                            <TableCell>Submitted</TableCell>
                            <TableCell>Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {subscriptions.map((subscription) => (
                            <TableRow key={subscription.id} hover>
                                <TableCell>
                                    <Box>
                                        <Typography variant="subtitle2">
                                            {subscription.organization_name || subscription.fund_name || 'Individual Application'}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            ID: {subscription.id}
                                        </Typography>
                                    </Box>
                                </TableCell>
                                <TableCell>
                                    <Box>
                                        <Typography variant="body2">
                                            {subscription.user?.name || 'No name'}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            {subscription.user?.email || 'No email'}
                                        </Typography>
                                    </Box>
                                </TableCell>
                                <TableCell>
                                    <Chip 
                                        label={formatStatus(subscription.status)} 
                                        color={getStatusColor(subscription.status)} 
                                        size="small"
                                    />
                                </TableCell>
                                <TableCell>
                                    <Typography variant="body2">
                                        {new Date(subscription.created_at).toLocaleDateString()}
                                    </Typography>
                                </TableCell>
                                <TableCell>
                                    <Button
                                        variant="outlined"
                                        size="small"
                                        startIcon={<VisibilityIcon />}
                                        onClick={() => handleViewSubscription(subscription.id)}
                                    >
                                        View
                                    </Button>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>

            <TablePagination
                component="div"
                count={total}
                page={page}
                onPageChange={handleChangePage}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                labelRowsPerPage="Subscriptions per page:"
            />
        </Container>
    );
};

export default ListInvestorSubscriptions; 